import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AuthUser from "../../../Hooks/authUser";
import Loading from "../../../Shared/Loading";
import DoctorAllPatientsTable from "../DoctorDashboardIndex/DoctorAllPatientsTable";

const DoctorPatients = () => {
  const { userInfo } = AuthUser();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination & Filter States
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(8); // items per page
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Search input debouncer
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(1); // Reset to first page on search
    }, 400);

    return () => clearTimeout(handler);
  }, [searchInput]);

  // Fetch paginated & filtered patients
  useEffect(() => {
    if (!userInfo?._id) return;
    setLoading(true);

    let url = `https://servercareseebd.careseebd.com/api/v1/user/specific?fieldName=referralId&fieldValue=${userInfo._id}&page=${currentPage}&limit=${limit}&search=${encodeURIComponent(debouncedSearch)}`;

    if (selectedStatus !== "all") {
      url += `&fieldName2=isActive&fieldValue2=${selectedStatus === "active" ? "true" : "false"}`;
    }

    fetch(url, {
      headers: {
        authorization: `Bearer ${localStorage.getItem("careSeeAccessToken")}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch patients list");
        }
        return res.json();
      })
      .then((data) => {
        setPatients(data?.data || []);
        setTotal(data?.total || 0);
        setTotalPages(data?.pages || 1);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [userInfo?._id, currentPage, limit, debouncedSearch, selectedStatus]);

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    setCurrentPage(1); // Reset to page 1 on filter
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Pagination display calculation
  const startIndex = (currentPage - 1) * limit + 1;
  const endIndex = Math.min(currentPage * limit, total);

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md my-5">
        <div className="flex items-center">
          <Icon icon="lucide:alert-circle" className="text-red-500 text-2xl mr-2" />
          <p className="text-red-700 font-semibold">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Search & Filter Header Container */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-4 bg-slate-50/50">
          <div>
            <h4 className="text-lg font-heading font-bold text-slate-800">Patients Directory</h4>
            <p className="text-sm text-slate-500">Search, filter, and manage all your practice's patient records</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto items-stretch sm:items-center">
            {/* Search Input */}
            <div className="relative flex-1 sm:flex-initial">
              <Icon icon="solar:magnifer-linear" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
              <input
                onChange={(e) => setSearchInput(e.target.value)}
                value={searchInput}
                className="w-full sm:w-60 pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-secondary transition text-sm"
                type="search"
                placeholder="Search patient name, email..."
              />
            </div>

            {/* Dropdown Status Select */}
            <div className="relative">
              <select
                onChange={handleStatusChange}
                value={selectedStatus}
                className="w-full sm:w-44 px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-secondary transition text-sm appearance-none bg-white pr-8 font-medium text-slate-700"
              >
                <option value="all">All Patients</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
              <Icon icon="solar:alt-arrow-down-linear" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>

            {/* Add New Patient Link */}
            <Link to="/doctorDashboard/doctor-add-new-patient" className="w-full sm:w-auto">
              <button className="w-full bg-secondary hover:bg-secondary/95 text-white py-2 px-4 rounded-xl flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-indigo-950/20 hover:-translate-y-0.5 duration-200 text-sm font-semibold">
                <Icon className="text-xl" icon="solar:user-plus-bold" />
                <span>Add Patient</span>
              </button>
            </Link>
          </div>
        </div>

        {/* Patients Table */}
        <div className="w-full overflow-x-auto">
          {loading ? (
            <div className="py-20"><Loading /></div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100 text-slate-400 text-[11px] font-bold tracking-wider uppercase bg-slate-50/20">
                  <th className="px-6 py-4">Patient Profile</th>
                  <th className="px-6 py-4">Contact Info</th>
                  <th className="px-6 py-4">Therapy Plan</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Date of Birth</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {patients.length > 0 ? (
                  patients.map((patient) => (
                    <DoctorAllPatientsTable key={patient._id} patient={patient} />
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="py-16 text-center text-slate-400">
                      <Icon icon="solar:user-block-outline" className="text-4xl mx-auto mb-2 text-slate-300" />
                      <p className="font-medium">No patient profiles found matching the criteria.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Footer */}
        {!loading && total > 0 && (
          <div className="p-5 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row justify-between items-center gap-4">
            <span className="text-xs font-medium text-slate-500">
              Showing <span className="font-bold text-slate-700">{startIndex}</span> to{" "}
              <span className="font-bold text-slate-700">{endIndex}</span> of{" "}
              <span className="font-bold text-slate-700">{total}</span> patients
            </span>

            <div className="flex items-center gap-1">
              {/* Prev Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 border border-slate-200 rounded-xl hover:bg-slate-100 text-slate-600 disabled:opacity-40 disabled:hover:bg-transparent transition cursor-pointer"
                title="Previous Page"
              >
                <Icon icon="solar:alt-arrow-left-linear" />
              </button>

              {/* Page Number Buttons */}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-9 h-9 flex items-center justify-center rounded-xl text-xs font-bold transition ${currentPage === page
                    ? "bg-secondary text-white shadow-md shadow-indigo-900/10"
                    : "border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-800"
                    }`}
                >
                  {page}
                </button>
              ))}

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 border border-slate-200 rounded-xl hover:bg-slate-100 text-slate-600 disabled:opacity-40 disabled:hover:bg-transparent transition cursor-pointer"
                title="Next Page"
              >
                <Icon icon="solar:alt-arrow-right-linear" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorPatients;
